import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../contexts/AuthProvider";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useCanModifyUsers } from "../../../hooks/useCanModifyUsers";
import { haveRolesChanged } from "../../../pages/Settings/DepartmentConfiguration/forms/adminRoles";
import {
	adminUsersQueryKey,
	canonicalUsername,
	fetchAdminUsersList,
	useAdminUsers,
	useSetAdminUserRoles,
	useUpdateAdminUsers,
} from "../../../pages/Settings/DepartmentConfiguration/forms/adminUsersHooks";
import type { AdminRole, AdminUser } from "../../../pages/Settings/DepartmentConfiguration/forms/types";
import { backendService } from "../../../services/backend.service";
import { trackErrorEvent, trackEvent } from "../../../utils/analytics";
import { guessAndFormatPhoneNumber } from "../../../utils/phoneAndEmailUtils";

export function useTeamAdmins() {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { chosenDepartment, afterScheduleWrite, showErrorSnackbar } = useHospitalData();
	const { userInfo } = useAuth();
	const queryClient = useQueryClient();
	const hasModifyUserPermission = useCanModifyUsers();

	const { data: adminUsers, isLoading } = useAdminUsers();
	const [newUsers, setNewUsers] = useState<AdminUser[]>([]);
	const [existingUsers, setExistingUsers] = useState<AdminUser[]>(adminUsers || []);
	const [originalUsers, setOriginalUsers] = useState<AdminUser[]>(adminUsers || []);
	const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
	const [pendingSave, setPendingSave] = useState<(() => Promise<void>) | null>(null);
	// Held across BOTH phases of the save. The two mutation flags cannot do this job: the refetch that
	// resolves new users' ids sits between them as a plain service call, so neither is pending while it
	// runs, and the roles pass fires N concurrent mutations through one observer that only tracks the
	// last one started. Without this the grid unlocks mid-save and a second click re-enters.
	const [isSavingAll, setIsSavingAll] = useState(false);

	useEffect(() => {
		setExistingUsers(adminUsers || []);
		setOriginalUsers(adminUsers || []);
	}, [adminUsers]);

	const { mutateAsync: updateAdminUsers, isPending: isSaving } = useUpdateAdminUsers({ existingUsers, newUsers });
	const { mutateAsync: setAdminUserRoles, isPending: isSavingRoles } = useSetAdminUserRoles();
	const { mutateAsync: removeAdminUser, isPending: isRemoving } = useMutation<void, Error, string>({
		mutationFn: async (user_id: string) => {
			if (!hasModifyUserPermission) {
				throw Error("Current user is not authorized to remove admin users");
			}
			const response = await backendService.removeAdminUserFromDepartment(chosenDepartment.department_id, user_id);
			if (response.status >= 400) {
				throw Error(response.statusText);
			}
		},
	});

	const validateAdminUsers = useCallback((): boolean => {
		const allUsers = [...newUsers, ...existingUsers];
		const invalidUsers = allUsers.filter((user) => !user.firstName || user.firstName.trim() === "");

		if (invalidUsers.length > 0) {
			showSnackbar("All admin users must have a first name", "error");
			return false;
		}
		return true;
	}, [newUsers, existingUsers, showSnackbar]);

	const hasLoginDetailsChanges = useCallback((): boolean => {
		for (const existingUser of existingUsers) {
			const originalUser = originalUsers.find((u) => u.id === existingUser.id);
			if (originalUser) {
				const currentUsername = existingUser.username?.trim().toLowerCase() ?? "";
				const originalUsername = originalUser.username?.trim().toLowerCase() ?? "";
				const usernameChanged = currentUsername !== originalUsername;

				const currentPhone = existingUser.phoneNumber?.trim() ?? "";
				const originalPhone = originalUser.phoneNumber?.trim() ?? "";
				const phoneChanged = currentPhone !== originalPhone;

				if (usernameChanged || phoneChanged) {
					return true;
				}
			}
		}
		return false;
	}, [existingUsers, originalUsers]);

	const queryKey = useMemo(() => adminUsersQueryKey(chosenDepartment.department_id, userInfo.id), [chosenDepartment.department_id, userInfo.id]);

	/**
	 * Roles have their own endpoint, so a save is two calls: permissions first, then one roles call
	 * per user whose roles changed.
	 *
	 * New rows have no id until the server creates them, and the roles endpoint needs one. The create
	 * response alone is not enough — it returns only newly inserted users, so an existing user added
	 * to this department is missing from it. So we refetch the list and match by username, falling
	 * back to the phone number for admins added without an email.
	 *
	 * Returns the usernames whose roles failed to save, so the caller can report a partial failure.
	 */
	const saveChangedRoles = useCallback(
		async (savedUsers: AdminUser[], attemptedNewUsers: AdminUser[]): Promise<string[]> => {
			const changedExisting = existingUsers.filter((user) => {
				const original = originalUsers.find((o) => o.id === user.id);
				return user.id && haveRolesChanged(user.adminRoles, original?.adminRoles);
			});
			// A new row only needs a roles call if it was actually given roles.
			const changedNew = attemptedNewUsers.filter((user) => (user.adminRoles?.length ?? 0) > 0);

			if (changedExisting.length === 0 && changedNew.length === 0) {
				return [];
			}

			// Skip admins with no email -- they would all key off the same "" and collide.
			const idByUsername = new Map(savedUsers.filter((user) => user.id && user.username).map((user) => [canonicalUsername(user.username), user.id as string]));
			// Fallback key for admins with no email. Both sides go through the same formatter because
			// the backend stores E164 and the grid holds whatever was typed.
			const idByPhone = new Map(
				savedUsers
					.filter((user) => user.id && user.phoneNumber)
					.map((user) => [guessAndFormatPhoneNumber(user.phoneNumber as string).formatted, user.id as string]),
			);
			const targets: { userId: string; roles: AdminRole[]; username: string }[] = [];

			// Only used to name the admin in the error snackbar, so fall back to the phone number
			// for admins who have no email.
			const label = (user: AdminUser) => user.username || user.phoneNumber || user.firstName;

			for (const user of changedExisting) {
				if (user.id) targets.push({ userId: user.id, roles: user.adminRoles, username: label(user) });
			}
			const unresolved: string[] = [];
			for (const user of changedNew) {
				// Email first, then phone. Check the canonical form, not the raw string: a whitespace-only
				// email is stored as no email at all, so it needs the phone path too.
				const canonical = canonicalUsername(user.username);
				const resolvedId = canonical
					? idByUsername.get(canonical)
					: user.phoneNumber
						? idByPhone.get(guessAndFormatPhoneNumber(user.phoneNumber).formatted)
						: undefined;
				if (resolvedId) {
					targets.push({ userId: resolvedId, roles: user.adminRoles, username: label(user) });
				} else {
					// Loud, not silent: the user was created but we cannot address them for the roles call.
					unresolved.push(label(user));
				}
			}

			const results = await Promise.allSettled(targets.map(({ userId, roles }) => setAdminUserRoles({ userId, roles })));
			const failed = results.flatMap((result, i) => (result.status === "rejected" ? [targets[i].username] : []));

			return [...unresolved, ...failed];
		},
		[existingUsers, originalUsers, setAdminUserRoles],
	);

	const performSave = useCallback(async () => {
		// Captured before the permissions call clears the draft rows, so the roles pass still knows
		// which brand-new users it has to resolve ids for.
		const attemptedNewUsers = newUsers;
		setIsSavingAll(true);
		try {
			trackEvent("Team admins - update admin users", {
				department_id: chosenDepartment.department_id,
				newUsers: newUsers,
				existingUsers: existingUsers,
			});

			const result = await updateAdminUsers();

			trackEvent("Team admins - admin users successfully updated", {
				department_id: chosenDepartment.department_id,
				updatedUsers: result.users,
				failedNotify: result.failedWelcomeEmails,
			});

			setNewUsers([]);

			// Re-read BEFORE the roles pass: this is what resolves a new row's real id (see
			// `saveChangedRoles`). `fetchQuery` rather than the service directly, so the fresh list also
			// lands in the cache the list query reads from.
			const refetched = await queryClient.fetchQuery({
				queryKey,
				queryFn: () => fetchAdminUsersList(chosenDepartment.department_id),
			});
			const failedRoleUsers = await saveChangedRoles(refetched, attemptedNewUsers);

			// The roles write changes what the grid should show, so pick it up before reporting success.
			await queryClient.invalidateQueries({ queryKey });

			if (failedRoleUsers.length > 0) {
				trackErrorEvent("Team admins - error updating admin roles", Error("Admin roles save failed"), {
					department_id: chosenDepartment.department_id,
					failedRoleUsers,
				});
				showErrorSnackbar(t("admin.roles.save.error"), { failedRoleUsers });
			} else {
				showSnackbar(t("department.updated.success", { departmentName: chosenDepartment.display_name }), "success");
			}

			if (result.failedWelcomeEmails.length > 0) {
				showSnackbar(`Failed to send welcome to: ${result.failedWelcomeEmails.join(", ")}`, "error");
			}

			await afterScheduleWrite({ allWeeks: true });
		} catch (err) {
			if (err instanceof Error) {
				const error = err.cause as AxiosError<{ detail: string }>;
				trackErrorEvent("Team admins - error updating admin users", err, {
					department_id: chosenDepartment.department_id,
					newUsers,
					existingUsers,
				});
				// Either call may have failed after the other succeeded, so resync the grid with what is
				// really stored instead of leaving the failed draft on screen.
				await queryClient.invalidateQueries({ queryKey });
				showErrorSnackbar(error?.response?.data?.detail || "Failed to update admin users", {}, error);
			}
		} finally {
			setIsSavingAll(false);
		}
	}, [
		chosenDepartment.department_id,
		chosenDepartment.display_name,
		existingUsers,
		newUsers,
		updateAdminUsers,
		saveChangedRoles,
		queryKey,
		showSnackbar,
		t,
		afterScheduleWrite,
		showErrorSnackbar,
		queryClient,
	]);

	/**
	 * Whether the save committed, so a caller can stay open when it did not.
	 *
	 * `false` = already saving, failed validation, or a pending login-details confirmation.
	 */
	const saveAdminUsers = useCallback(async (): Promise<boolean> => {
		// A save is a two-phase write; re-entering it would race the roles POSTs against the same rows.
		if (isSavingAll) {
			return false;
		}

		if (!validateAdminUsers()) {
			return false;
		}

		if (hasLoginDetailsChanges()) {
			setPendingSave(() => performSave);
			setConfirmationDialogOpen(true);
			return false;
		}
		await performSave();
		return true;
	}, [isSavingAll, validateAdminUsers, hasLoginDetailsChanges, performSave]);

	const handleConfirmSave = useCallback(async () => {
		setConfirmationDialogOpen(false);
		if (pendingSave) {
			await pendingSave();
			setPendingSave(null);
		}
	}, [pendingSave]);

	const handleCancelSave = useCallback(() => {
		setConfirmationDialogOpen(false);
		setExistingUsers((prev) => {
			return prev.map((user) => {
				const originalUser = originalUsers.find((u) => u.id === user.id);
				if (originalUser) {
					return {
						...user,
						username: originalUser.username,
						phoneNumber: originalUser.phoneNumber,
					};
				}
				return user;
			});
		});
		setPendingSave(null);
	}, [originalUsers]);

	const removeUser = async (userId: string) => {
		try {
			trackEvent("Team admins - remove admin user from department", {
				department_id: chosenDepartment.department_id,
				user_id: userId,
			});

			await removeAdminUser(userId);

			trackEvent("Team admins - admin user successfully removed from department", {
				department_id: chosenDepartment.department_id,
				user_id: userId,
			});

			// Invalidate and refetch admin users to get the updated list
			await queryClient.invalidateQueries({
				queryKey,
			});

			showSnackbar("Admin user removed from department successfully", "success");
			await afterScheduleWrite({ allWeeks: true });
		} catch (err) {
			if (err instanceof Error) {
				const error = err.cause as AxiosError<{ detail: string }>;
				trackErrorEvent("Team admins - error removing admin user from department", err, {
					department_id: chosenDepartment.department_id,
					user_id: userId,
				});
				showErrorSnackbar(error.response?.data?.detail || "Failed to remove admin user from department", {}, error);
			}
		}
	};

	return {
		existingUsers,
		setExistingUsers,
		originalUsers,
		newUsers,
		setNewUsers,
		isLoading,
		// The roles call is the second half of the same save, so the grid stays locked through both.
		isSaving: isSavingAll || isSaving || isSavingRoles,
		isRemoving,
		saveAdminUsers,
		removeUser,
		confirmationDialogOpen,
		handleConfirmSave,
		handleCancelSave,
	};
}
